import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, MessageSquare } from 'lucide-react';
import { useTicket, useAddComment } from '../hooks/useTickets';
import { StatusBadge } from '../components/tickets/StatusBadge';
import { PriorityBadge } from '../components/tickets/PriorityBadge';
import { CommentThread } from '../components/tickets/CommentThread';
import { CommentEditor } from '../components/tickets/CommentEditor';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: ticket, isLoading, isError } = useTicket(id);
  const addComment = useAddComment();

  const handleAddComment = (body: string) => {
    addComment.mutate({ ticketId: id!, data: { body } });
  };

  /* ------------------------------------------------------------------ */
  /* Loading state                                                       */
  /* ------------------------------------------------------------------ */
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-ems-green animate-spin" />
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Not found state                                                     */
  /* ------------------------------------------------------------------ */
  if (isError || !ticket) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-4">
        <p className="text-ems-text-secondary text-lg">Ticket not found</p>
        <button
          onClick={() => navigate('/tickets')}
          className="flex items-center gap-2 text-sm text-ems-green hover:text-ems-green-bright transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Tickets
        </button>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Detail metadata rows                                                */
  /* ------------------------------------------------------------------ */
  const detailRows: { label: string; value: React.ReactNode }[] = [
    {
      label: 'Submitted by',
      value: (
        <span className="text-ems-text-primary text-sm">
          {ticket.submitter_name || '\u2014'}
        </span>
      ),
    },
    {
      label: 'Assigned to',
      value: (
        <span className="text-ems-text-primary text-sm">
          {ticket.assignee_name || 'Unassigned'}
        </span>
      ),
    },
    {
      label: 'Status',
      value: <StatusBadge status={ticket.status} />,
    },
    {
      label: 'Priority',
      value: <PriorityBadge priority={ticket.priority} />,
    },
    {
      label: 'Category',
      value: (
        <span className="text-ems-text-primary text-sm">
          {ticket.category_name || '\u2014'}
        </span>
      ),
    },
    {
      label: 'Site',
      value: (
        <span className="text-ems-text-primary text-sm">
          {ticket.site_name || '\u2014'}
        </span>
      ),
    },
    {
      label: 'Created',
      value: (
        <span className="text-ems-text-primary text-sm">
          {formatDate(ticket.created_at)}
        </span>
      ),
    },
    {
      label: 'Last updated',
      value: (
        <span className="text-ems-text-primary text-sm">
          {formatDate(ticket.updated_at)}
        </span>
      ),
    },
  ];

  if (ticket.resolved_at) {
    detailRows.push({
      label: 'Resolved',
      value: (
        <span className="text-ems-text-primary text-sm">
          {formatDate(ticket.resolved_at)}
        </span>
      ),
    });
  }

  const commentCount = ticket.comments?.length ?? 0;

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */
  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate('/tickets')}
        className="flex items-center gap-2 text-sm text-ems-text-secondary hover:text-ems-text-primary transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Tickets
      </button>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ---------------------------------------------------------- */}
        {/* Left column (wide)                                          */}
        {/* ---------------------------------------------------------- */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket header */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-mono text-ems-text-muted bg-ems-bg-card-hover px-2 py-0.5 rounded">
                #{ticket.ticket_number}
              </span>
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
            </div>
            <h1 className="text-xl font-bold text-ems-text-primary">
              {ticket.subject}
            </h1>
          </div>

          {/* Description card */}
          <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
            <h2 className="text-xs font-semibold text-ems-text-muted uppercase tracking-wider mb-3">
              Description
            </h2>
            <p className="text-sm text-ems-text-secondary leading-relaxed whitespace-pre-wrap">
              {ticket.description}
            </p>
          </div>

          {/* Conversation section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-ems-text-muted" />
              <h2 className="text-sm font-semibold text-ems-text-primary">
                Conversation ({commentCount})
              </h2>
            </div>

            <CommentThread comments={ticket.comments || []} />

            <CommentEditor
              onSubmit={handleAddComment}
              submitting={addComment.isPending}
            />
          </div>
        </div>

        {/* ---------------------------------------------------------- */}
        {/* Right column (narrow)                                       */}
        {/* ---------------------------------------------------------- */}
        <div className="lg:col-span-1">
          <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
            <h2 className="text-xs font-semibold text-ems-text-muted uppercase tracking-wider mb-4">
              Details
            </h2>
            <div className="divide-y divide-ems-border">
              {detailRows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between py-2.5"
                >
                  <span className="text-xs text-ems-text-muted">
                    {row.label}
                  </span>
                  {row.value}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
